<template>
  <section>
    <b-alert class="text-center" v-if="loadFailed" show variant="danger">
      Ticket not found! Are you sure you are logged in with the correct account?
    </b-alert>

    <div v-else-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
      <p class="mt-3 text-muted">Loading ticket...</p>
    </div>

    <div class="row" v-else-if="ticket">
      <!-- Main Chat Area -->
      <div class="col-12 col-lg-8 col-xl-9">
        <b-card no-body class="chat-card">
          <!-- Card Header with ticket info -->
          <template v-slot:header>
            <div class="d-flex justify-content-between align-items-center">
              <router-link
                :to="{ name: 'Support.NewTickets.All' }"
                class="btn btn-outline-secondary btn-sm mr-1"
              >
                <i class="fa fa-arrow-left mr-1"></i> Back to List
              </router-link>
              <div class="border-left pl-1">
                <h5 class="mb-1">{{ ticket.ticketNumber }} - {{ ticket.name }}</h5>
                <div class="d-flex align-items-center">
                  <b-badge :variant="getStatusVariant(ticket.status, ticket.subStatus)" class="mr-2">
                    {{ getStatusLabel(ticket.status, ticket.subStatus) }}
                  </b-badge>
                  <b-badge :variant="getPriorityVariant(ticket.priority)">
                    {{ getPriorityLabel(ticket.priority) }}
                  </b-badge>
                </div>
              </div>
            </div>
          </template>

          <!-- Message Input at Top -->
          <div class="p-3 border-bottom bg-light" v-if="!isTicketClosed">
            <b-form-group label="Leave a comment:" class="mb-2">
              <markdwn-editor
                ref="toastuiCommentEditor"
                :options="editorOptions"
                height="150px"
                initialEditType="wysiwyg"
                previewStyle="tab"
              ></markdwn-editor>
            </b-form-group>
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <b-button
                  v-if="!showAttachments"
                  variant="outline-secondary"
                  size="sm"
                  @click="showAttachments = true"
                >
                  <i class="fa fa-paperclip mr-1"></i> Add Attachments
                </b-button>
              </div>
              <b-button
                variant="primary"
                :disabled="postingComment"
                @click="postComment"
              >
                <span v-if="postingComment">Sending...</span>
                <span v-else><i class="fa fa-paper-plane mr-1"></i> Send Message</span>
              </b-button>
            </div>
            <div v-if="showAttachments" class="mt-3">
              <UploadImages :max="5" @changed="handleImages" />
            </div>
          </div>
          <div v-else class="p-3 border-bottom">
            <b-alert show variant="warning" class="mb-0 d-flex justify-content-between align-items-center">
              <span>
                <i class="fa fa-lock mr-1"></i> This ticket is closed. No further messages can be added.
              </span>
              <b-button
                variant="warning"
                size="sm"
                @click="showReopenModal = true"
              >
                <i class="fa fa-refresh mr-1"></i> Reopen Ticket
              </b-button>
            </b-alert>
          </div>

          <!-- Reopen Modal -->
          <b-modal
            v-model="showReopenModal"
            title="Reopen Ticket"
            centered
            @ok="reopenTicket"
            :ok-disabled="!reopenReason.trim() || reopeningTicket"
            :ok-title="reopeningTicket ? 'Reopening...' : 'Reopen'"
            ok-variant="warning"
          >
            <b-form-group label="Why are you reopening this ticket?">
              <b-form-textarea
                v-model="reopenReason"
                placeholder="Enter reason for reopening..."
                rows="3"
              ></b-form-textarea>
            </b-form-group>
          </b-modal>

          <!-- Messages List -->
          <div class="messages-container p-3">
            <div
              v-for="message in allMessages"
              :key="message.id"
              class="message-card mb-3"
            >
              <b-card no-body :class="message.isOriginal ? 'bg-primary-light' : 'border'">
                <template v-slot:header>
                  <div class="d-flex px-2 w-100">
                    <div class="d-flex align-items-center">
                      <b-avatar
                        size="2em"
                        :variant="message.isOriginal ? 'primary' : (message.userId === currentUserId ? 'info' : 'success')"
                        :src="message.authorAvatar"
                        class="mr-2"
                      ></b-avatar>
                      <span class="font-weight-bold">{{ message.authorName }}</span>
                      <b-badge v-if="message.isOriginal" variant="primary" class="ml-2">Original</b-badge>
                    </div>
                    <div class="d-flex align-items-center ml-auto">
                      <small class="text-muted mr-2">{{ formatDate(message.createDateTimeUtc) }}</small>
                      <div v-if="!message.isOriginal" style="position: relative;">
                        <b-button
                          variant="light"
                          size="sm"
                          class="border p-1"
                          @click="toggleEmojiDialog(message.id)"
                        >
                          <span style="font-size: 14px;">😃</span>
                        </b-button>
                        <div
                          v-if="showEmojiDialogFor === message.id"
                          class="emoji-picker-container"
                        >
                          <VEmojiPicker
                            :customEmojis="customEmojiList"
                            :emojisByRow="7"
                            :emojiSize="26"
                            :style="{ width: '310px', height: '400px' }"
                            labelSearch="Search"
                            lang="en-US"
                            @select="onSelectEmoji(message.id, $event)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
                <b-card-body class="py-2 px-3">
                  <div class="vue-prosemirror ticket-message-content">
                    <vue-markdown>{{ message.message }}</vue-markdown>
                  </div>
                  <div v-if="message.reactions && message.reactions.length" class="mt-2 pt-2 border-top">
                    <span
                      v-for="(reaction, idx) in message.reactions"
                      :key="idx"
                      class="badge badge-light border mr-1 px-2 py-1"
                      v-b-tooltip.hover
                      :title="reaction.byUser + ' reacted'"
                    >
                      {{ getReactionEmoji(reaction.name) }}
                    </span>
                  </div>
                </b-card-body>
              </b-card>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Sidebar with ticket details -->
      <aside class="col-12 col-lg-4 col-xl-3">
        <!-- Ticket Details Card -->
        <b-card header="Ticket Details" class="mb-3">
          <div class="d-flex">
            <div class="col-6 p-0">
              <div class="detail-row mb-3">
                <small class="text-muted d-block">Status</small>
                <b-badge :variant="getStatusVariant(ticket.status, ticket.subStatus)" class="px-2 py-1">
                  {{ getStatusLabel(ticket.status, ticket.subStatus) }}
                </b-badge>
              </div>
            </div>
            <div class="col-6 p-0 pl-1">
              <div class="detail-row mb-3">
                <small class="text-muted d-block">Priority</small>
                <b-form-select
                  v-if="is('superadmin') && !isTicketClosed"
                  v-model="ticket.priority"
                  :options="priorityList"
                  size="sm"
                  @change="changePriority"
                ></b-form-select>
                <b-badge v-else :variant="getPriorityVariant(ticket.priority)" class="px-2 py-1">
                  {{ getPriorityLabel(ticket.priority) }}
                </b-badge>
              </div>
            </div>
          </div>

          <div class="detail-row mb-3">
            <small class="text-muted d-block">Type</small>
            <span>{{ ticket.type }}</span>
          </div>

          <div class="detail-row mb-3">
            <small class="text-muted d-block">Created By</small>
            <span>{{ ticket.userFullName }}</span>
            <small class="text-muted d-block">{{ ticket.userEmail }}</small>
          </div>

          <div class="detail-row mb-3">
            <small class="text-muted d-block">Created On</small>
            <span>{{ formatDate(ticket.createDateTimeUtc) }}</span>
          </div>

          <div class="detail-row mb-3">
            <small class="text-muted d-block">Last Update</small>
            <span>{{ formatDate(ticket.modifiedDateTimeUtc) }}</span>
          </div>

          <div class="detail-row mb-3" v-if="ticket.completedDateTimeUtc">
            <small class="text-muted d-block">Closed On</small>
            <span>{{ formatDate(ticket.completedDateTimeUtc) }}</span>
          </div>

          <div class="detail-row" v-if="is('developer') && ticket.linearIssueId">
            <small class="text-muted d-block">Linear Issue</small>
            <span class="text-success"><i class="fa fa-check mr-1"></i> Synced</span>
          </div>
        </b-card>

        <!-- Actions Card -->
        <b-card header="Actions" class="mb-3">
          <b-form-group
            label="Change Status:"
            v-if="!isTicketClosed && is('superadmin') && (ticket.status === 'backlog' || ticket.status === 'triage')"
          >
            <b-form-select
              v-model="ticket.status"
              :options="statusList"
              size="sm"
              @change="changeStatus"
            ></b-form-select>
          </b-form-group>

          <b-button
            v-if="ticket.subStatus === 'QA' && ticket.userId === currentUserId"
            variant="success"
            block
            @click="approveQA"
          >
            <i class="fa fa-check mr-1"></i> Approve QA
          </b-button>

          <b-button
            v-if="(ticket.status === 'backlog' || ticket.status === 'triage') && !isEditing"
            variant="warning"
            block
            @click="isEditing = true"
          >
            <i class="fa fa-edit mr-1"></i> Edit Ticket
          </b-button>

          <b-button
            v-if="isEditing"
            variant="primary"
            block
            @click="saveEdits"
          >
            <i class="fa fa-save mr-1"></i> Save Changes
          </b-button>
        </b-card>

        <!-- Collaboration Card -->
        <b-card header="Collaborators">
          <div v-if="ticket.collaboratorWithUsers && ticket.collaboratorWithUsers.length > 0">
            <div
              v-for="usr in ticket.collaboratorWithUsers"
              :key="usr.id"
              class="border rounded p-2 mb-2"
            >
              <i class="fa fa-user mr-2"></i>
              <span>{{ usr.fullName }}</span>
              <small class="text-muted d-block ml-4">{{ usr.email }}</small>
            </div>
          </div>
          <div v-else class="text-muted text-center py-2">
            <small>No collaborators</small>
          </div>

          <hr>

          <b-form-group class="mb-2">
            <b-v-select
              v-model="shareWithUserId"
              :searchable="true"
              :options="collaboratorOptions"
              placeholder="Select user..."
            />
          </b-form-group>
          <b-button
            block
            variant="outline-primary"
            size="sm"
            :disabled="!shareWithUserId"
            @click="shareWith"
          >
            <i class="fa fa-share mr-1"></i> Share
          </b-button>
        </b-card>
      </aside>
    </div>
  </section>
</template>

<script>
import { Editor } from '@toast-ui/vue-editor';
import CustomEmojis from '@/helpers/custom-emojis.js';
import { VEmojiPicker } from 'v-emoji-picker';
import UploadImages from 'vue-upload-drop-images';
import VueMarkdown from 'vue-markdown';
import { myCommonDataMixin } from '@/helpers/utils/vueMixins.js';

var refOfHttp = null;
var refOfEditor = null;

var addImageBlobHook = function(blob, callback) {
  if (refOfEditor && refOfHttp) {
    var fileName = blob.name;
    var reader = new FileReader();
    reader.onload = function() {
      var formData = new FormData();
      formData.append('formFile', blob);
      formData.append('extension', fileName.split('.').pop());
      var headers = { 'Content-Type': 'multipart/form-data' };
      refOfHttp.post('/api/common/tickets/imageUpload', formData, { headers }).then(function(resp) {
        var oldContent = refOfEditor.invoke('getMarkdown');
        refOfEditor.invoke('setMarkdown', oldContent + '\r\n\r\n![' + fileName + '](' + resp.data.url + ')\r\n\r\n');
      });
    };
    reader.readAsDataURL(blob);
  }
  callback();
  return false;
};

export default {
  name: 'NewTicketDetails',
  mixins: [myCommonDataMixin],
  components: {
    'markdwn-editor': Editor,
    VEmojiPicker,
    UploadImages,
    'vue-markdown': VueMarkdown
  },

  props: {
    id: {
      type: String,
      required: true
    }
  },

  data: function() {
    return {
      ticket: null,
      loading: true,
      loadFailed: false,
      isEditing: false,
      postingComment: false,
      showAttachments: false,
      uploadedFiles: [],
      shareWithUserId: '',
      usersAll: [],
      showEmojiDialogFor: '',
      customEmojiList: CustomEmojis.list,
      refreshHandler: null,
      reopenReason: '',
      reopeningTicket: false,
      showReopenModal: false,

      editorOptions: {
        hooks: {
          addImageBlobHook: addImageBlobHook
        },
        minHeight: '100px',
        language: 'en-US',
        useCommandShortcut: true,
        hideModeSwitch: true,
        toolbarItems: [
          ['bold', 'italic', 'strike'],
          ['ul', 'ol'],
          ['link', 'image'],
          ['code', 'codeblock']
        ]
      },

      priorityList: [
        { value: 1, text: 'Low' },
        { value: 2, text: 'Medium' },
        { value: 3, text: 'High' },
        { value: 4, text: 'Urgent' }
      ],

      statusList: [
        { value: 'backlog', text: 'Draft' },
        { value: 'triage', text: 'Triage' }
      ]
    };
  },

  computed: {
    currentUserId: function() {
      return this.$store.getters['account/getUserData'].id;
    },
    isTicketClosed: function() {
      return this.ticket && (this.ticket.status === 'completed' || this.ticket.status === 'canceled');
    },
    allMessages: function() {
      var self = this;
      if (!self.ticket) return [];

      var messages = (self.ticket.ticketMessages || []).slice();

      // Add the original message as the last item (oldest)
      if (self.ticket.message) {
        messages.push({
          id: 'original-message',
          message: self.ticket.message,
          authorName: self.ticket.userFullName,
          authorEmail: self.ticket.userEmail,
          userId: self.ticket.userId,
          createDateTimeUtc: self.ticket.createDateTimeUtc,
          isOriginal: true,
          reactions: []
        });
      }

      return messages;
    },
    collaboratorOptions: function() {
      var self = this;
      var existingIds = (self.ticket && self.ticket.collaboratorUserIds) || [];
      var options = [];
      self.usersAll.forEach(function(usr) {
        if (!existingIds.includes(usr.id)) {
          options.push({
            value: usr.id,
            text: usr.fullName + ' (' + usr.email + ')'
          });
        }
      });
      return options;
    }
  },

  watch: {
    id: {
      handler: function(newVal) {
        if (newVal) {
          this.loadTicket(newVal);
        }
      },
      immediate: true
    }
  },

  mounted: function() {
    var self = this;
    this.loadUsers();
    refOfHttp = this.$http;

    this.$nextTick(function() {
      if (self.$refs.toastuiCommentEditor) {
        refOfEditor = self.$refs.toastuiCommentEditor;
      }
    });

    // Auto-refresh every 30 seconds
    this.refreshHandler = setInterval(function() {
      if (!self.isEditing && self.id) {
        self.loadTicket(self.id, true);
      }
    }, 30000);
  },

  beforeDestroy: function() {
    if (this.refreshHandler) {
      clearInterval(this.refreshHandler);
    }
  },

  methods: {
    loadTicket: function(id, silent) {
      var self = this;
      if (!silent) {
        self.loading = true;
      }
      self.loadFailed = false;

      self.$http
        .get('/api/common/tickets/' + id)
        .then(function(resp) {
          self.ticket = resp.data;
        })
        .catch(function(err) {
          console.error('Failed to load ticket', err);
          if (!silent) {
            self.loadFailed = true;
          }
        })
        .finally(function() {
          self.loading = false;
        });
    },

    loadUsers: function() {
      var self = this;
      self.$http
        .get('/api/userspublic/collab')
        .then(function(resp) {
          self.usersAll = resp.data;
        })
        .catch(function(err) {
          console.error('Failed to load users', err);
        });
    },

    postComment: function() {
      var self = this;
      if (!self.$refs.toastuiCommentEditor) return;

      var message = self.$refs.toastuiCommentEditor.invoke('getMarkdown');
      if (!message || !message.trim()) {
        self.$swal('Error', 'Please enter a message', 'warning');
        return;
      }

      // Handle attachments
      if (self.uploadedFiles.length > 0) {
        message += '\n\n**Attachments:**';
        self.uploadedFiles.forEach(function(file) {
          message += '\n\n![' + file.name + '](' + file.url + ')';
        });
      }

      self.postingComment = true;
      var userData = self.$store.getters['account/getUserData'];
      var payload = {
        ticketId: self.ticket.id,
        message: message,
        authorEmail: userData.email,
        authorName: userData.fullName,
        userId: userData.id
      };

      self.$http
        .post('/api/common/ticketMessages', payload)
        .then(function(resp) {
          var newMessage = resp.data;
          if (!self.ticket.ticketMessages) {
            self.ticket.ticketMessages = [];
          }
          self.ticket.ticketMessages.unshift(newMessage);
          self.$refs.toastuiCommentEditor.invoke('setMarkdown', '');
          self.uploadedFiles = [];
          self.showAttachments = false;
        })
        .catch(function(err) {
          console.error('Failed to post comment', err);
          self.$swal('Error', 'Failed to send message', 'error');
        })
        .finally(function() {
          self.postingComment = false;
        });
    },

    handleImages: function(files) {
      var self = this;
      files.forEach(function(file) {
        if (!self.uploadedFiles.find(function(f) { return f.name === file.name; })) {
          var formData = new FormData();
          formData.append('formFile', file);
          var headers = { 'Content-Type': 'multipart/form-data' };
          self.$http.post('/api/common/tickets/imageUpload', formData, { headers }).then(function(resp) {
            self.uploadedFiles.push({
              name: file.name,
              size: file.size,
              url: resp.data.url
            });
          });
        }
      });
    },

    changePriority: function() {
      var self = this;
      self.$http
        .post('/api/common/tickets/changePriority/' + self.ticket.id + '/' + self.ticket.priority)
        .then(function() {
          self.$swal('Success', 'Priority updated', 'success');
        })
        .catch(function(err) {
          console.error('Failed to change priority', err);
        });
    },

    changeStatus: function() {
      var self = this;
      self.$http
        .post('/api/common/tickets/changeStatus/' + self.ticket.id + '/' + self.ticket.status)
        .then(function() {
          self.$swal('Success', 'Status updated', 'success');
        })
        .catch(function(err) {
          console.error('Failed to change status', err);
        });
    },

    approveQA: function() {
      var self = this;
      self.ticket.subStatus = 'Approved';
      self.ticket.status = 'completed';
      self.saveTicket().then(function() {
        self.$swal('Success', 'Ticket approved and closed', 'success');
      });
    },

    saveEdits: function() {
      var self = this;
      self.isEditing = false;
      self.saveTicket().then(function() {
        self.$swal('Success', 'Changes saved', 'success');
      });
    },

    saveTicket: function() {
      var self = this;
      var payload = JSON.parse(JSON.stringify(self.ticket));
      payload.ticketMessages = null;
      payload.collaboratorWithUsers = null;

      return self.$http
        .post('/api/common/tickets/edit', payload)
        .then(function(resp) {
          self.ticket = resp.data;
        })
        .catch(function(err) {
          console.error('Failed to save ticket', err);
          self.$swal('Error', 'Failed to save changes', 'error');
        });
    },

    shareWith: function() {
      var self = this;
      if (!self.shareWithUserId) return;

      var user = self.usersAll.find(function(u) { return u.id === self.shareWithUserId; });
      if (!user) return;

      if (!self.ticket.collaboratorUserIds) {
        self.ticket.collaboratorUserIds = [];
      }
      if (!self.ticket.collaboratorWithUsers) {
        self.ticket.collaboratorWithUsers = [];
      }

      self.ticket.collaboratorUserIds.push(self.shareWithUserId);
      self.ticket.collaboratorWithUsers.push(JSON.parse(JSON.stringify(user)));
      self.shareWithUserId = '';

      self.saveTicket();
    },

    reopenTicket: function(bvModalEvt) {
      var self = this;
      if (bvModalEvt) {
        bvModalEvt.preventDefault();
      }
      if (!self.reopenReason.trim() || !self.ticket) return;

      self.reopeningTicket = true;
      var payload = {
        id: self.ticket.id,
        reason: self.reopenReason
      };

      self.$http
        .post('/api/common/tickets/reopen', payload)
        .then(function(resp) {
          self.ticket = resp.data;
          self.reopenReason = '';
          self.showReopenModal = false;
          self.$swal('Success', 'Ticket has been reopened', 'success');
        })
        .catch(function(err) {
          console.error('Failed to reopen ticket', err);
          self.$swal('Error', 'Failed to reopen ticket', 'error');
        })
        .finally(function() {
          self.reopeningTicket = false;
        });
    },

    toggleEmojiDialog: function(messageId) {
      if (this.showEmojiDialogFor === messageId) {
        this.showEmojiDialogFor = '';
      } else {
        this.showEmojiDialogFor = messageId;
      }
    },

    onSelectEmoji: function(messageId, emoji) {
      var self = this;
      var emojiName = emoji.aliases[0];

      self.$http
        .post('/api/common/ticketMessages/leaveReaction/' + messageId + '/' + emojiName)
        .then(function(resp) {
          var message = self.ticket.ticketMessages.find(function(m) { return m.id === messageId; });
          if (message && resp.data.reaction) {
            if (!message.reactions) {
              message.reactions = [];
            }
            message.reactions.push(resp.data.reaction);
          }
          self.showEmojiDialogFor = '';
        })
        .catch(function(err) {
          console.error('Failed to add reaction', err);
        });
    },

    getReactionEmoji: function(name) {
      var reactInfo = this.customEmojiList.find(function(a) {
        return a.aliases.includes(name);
      });
      return reactInfo ? reactInfo.data : name;
    },

    formatDate: function(dateStr) {
      if (!dateStr) return '';
      var date = dateStr;
      if (!dateStr.endsWith('Z')) {
        date = dateStr + 'Z';
      }
      return this.$moment(date).format('MMM Do YY, h:mm a');
    },

    getPriorityLabel: function(priority) {
      var labels = { 0: 'Not Set', 1: 'Low', 2: 'Medium', 3: 'High', 4: 'Urgent' };
      return labels[priority] || 'Not Set';
    },

    getPriorityVariant: function(priority) {
      var variants = { 0: 'secondary', 1: 'secondary', 2: 'info', 3: 'warning', 4: 'danger' };
      return variants[priority] || 'secondary';
    },

    getStatusLabel: function(status, subStatus) {
      if (subStatus && subStatus !== '') return subStatus;
      var labels = {
        'triage': 'Triage', 'backlog': 'Backlog', 'unstarted': 'Open',
        'started': 'In Progress', 'canceled': 'Cancelled', 'completed': 'Completed'
      };
      return labels[status] || status;
    },

    getStatusVariant: function(status, subStatus) {
      if (subStatus && subStatus !== '') return 'info';
      var variants = {
        'triage': 'warning', 'backlog': 'secondary', 'unstarted': 'primary',
        'started': 'info', 'canceled': 'danger', 'completed': 'success'
      };
      return variants[status] || 'secondary';
    }
  }
};
</script>

<style scoped>
.chat-card {
  min-height: 600px;
}

.messages-container {
  max-height: calc(100vh - 400px);
  overflow-y: auto;
}

.message-card .card-header {
  padding: 0.5rem 1rem;
  background-color: #f8f9fa;
}

.message-card .card-body {
  padding: 0.75rem 1rem;
}

.emoji-picker-container {
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  border-radius: 8px;
  overflow: hidden;
}

.detail-row {
  border-bottom: 1px solid #eee;
  padding-bottom: 0.75rem;
}

.detail-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
</style>
